import { Head } from 'vite-react-ssg';
import { TrackedLink } from "@/components/TrackedLink";
import { Link } from "react-router-dom";
import { Search, Brain, Zap, DollarSign, CheckCircle, XCircle, Chrome } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DeepseekVsPerplexity = () => {
  const faqItems = [
    {
      question: "What is the main difference between DeepSeek and Perplexity?",
      answer: "DeepSeek is an open-source AI model focused on reasoning, coding, and general-purpose AI tasks. Perplexity is an AI-powered search engine that combines LLMs with real-time web search to provide cited, up-to-date answers. DeepSeek excels at deep reasoning, while Perplexity excels at research and finding current information."
    },
    {
      question: "Which is better for research: DeepSeek or Perplexity?",
      answer: "Perplexity is better for research requiring current information and sources. It searches the web in real-time and provides citations. DeepSeek is better for analytical reasoning and deep thinking about concepts, but doesn't have built-in web search capabilities."
    },
    {
      question: "Is DeepSeek or Perplexity better for coding?",
      answer: "DeepSeek is significantly better for coding tasks. DeepSeek Coder and DeepSeek R1 are specifically optimized for code generation, debugging, and software development. Perplexity can help find coding solutions online but isn't designed as a coding assistant."
    },
    {
      question: "Can I use DeepSeek and Perplexity together?",
      answer: "Yes, they complement each other well. Use Perplexity for research and finding current information with sources, then use DeepSeek for deep analysis, reasoning, and implementation tasks like coding."
    },
    {
      question: "Which is more cost-effective: DeepSeek or Perplexity?",
      answer: "DeepSeek is more cost-effective, especially since it's open-source and can run locally for free. DeepSeek's API is also very affordable. Perplexity offers a free tier but Pro features require a $20/month subscription."
    },
    {
      question: "Does Perplexity use DeepSeek models?",
      answer: "Yes, Perplexity offers DeepSeek R1 as one of its available models. Perplexity Pro users can access DeepSeek R1 for reasoning tasks, combining DeepSeek's reasoning with Perplexity's search capabilities."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const comparisonSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "DeepSeek vs Perplexity: Complete AI Comparison 2025",
    "description": "Comprehensive comparison of DeepSeek and Perplexity AI. Compare features, pricing, use cases, and capabilities.",
    "author": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-12-20"
  };

  return (
    <>
      <Head>
        <title>DeepSeek vs Perplexity (2026) — Reasoning vs AI Search</title>
        <meta 
          name="description" 
          content="DeepSeek vs Perplexity in 2026: reasoning &amp; coding power vs AI search with citations. Honest comparison of features, pricing and best use cases." 
        />
        <meta name="keywords" content="DeepSeek vs Perplexity, Perplexity vs DeepSeek, AI search comparison, DeepSeek Perplexity, AI comparison 2026" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-vs-perplexity" />
        
        <meta property="og:title" content="DeepSeek vs Perplexity (2026) — Reasoning vs AI Search" />
        <meta property="og:description" content="DeepSeek vs Perplexity in 2026: full comparison of step-by-step reasoning vs cited AI search, accuracy, pricing, free tiers and the best tool for research, coding and chat." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-vs-perplexity" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek vs Perplexity (2026) — Reasoning vs AI Search" />
        <meta name="twitter:description" content="DeepSeek vs Perplexity in 2026: reasoning vs cited search, accuracy, pricing and the best tool for research, coding and chat." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(comparisonSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">DeepSeek vs Perplexity</h1>
          <p className="text-xl text-muted-foreground">
            AI reasoning model vs AI search engine. Two different approaches to AI assistance — understand when to use each.
          </p>
        </div>

        {/* Quick Comparison */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Brain className="h-10 w-10 text-primary mr-3" />
                <h2 className="text-2xl font-bold">DeepSeek</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Open-source AI model specializing in deep reasoning, mathematics, and code generation. Runs locally or via API.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Advanced reasoning & chain-of-thought
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Excellent code generation
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Open-source & local deployment
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Very cost-effective API
                </li>
              </ul>
            </div>

            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Search className="h-10 w-10 text-blue-500 mr-3" />
                <h2 className="text-2xl font-bold">Perplexity</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                AI-powered search engine that provides answers with real-time web search and source citations.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Real-time web search
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Source citations included
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Great for research
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Multiple model options
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Detailed Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Feature</TableHead>
                  <TableHead className="text-center">DeepSeek</TableHead>
                  <TableHead className="text-center">Perplexity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Primary Purpose</TableCell>
                  <TableCell className="text-center">Reasoning & Coding</TableCell>
                  <TableCell className="text-center">AI Search & Research</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Web Search</TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Source Citations</TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Open Source</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Local Deployment</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Math & Reasoning</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Good (uses LLMs)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Code Generation</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Basic</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Real-time Info</TableCell>
                  <TableCell className="text-center">Training cutoff</TableCell>
                  <TableCell className="text-center">Live web access</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Free Tier</TableCell>
                  <TableCell className="text-center">Generous</TableCell>
                  <TableCell className="text-center">Limited</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Pro Pricing</TableCell>
                  <TableCell className="text-center">Pay-per-use API</TableCell>
                  <TableCell className="text-center">$20/month</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">When to Use Each</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Brain className="h-6 w-6 text-primary mr-2" />
                Choose DeepSeek When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Writing or debugging code
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Solving complex math problems
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Deep analysis and reasoning tasks
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Privacy-sensitive applications
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Cost-sensitive high-volume usage
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Search className="h-6 w-6 text-blue-500 mr-2" />
                Choose Perplexity When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Researching current events or news
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Need sources and citations
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Fact-checking information
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Exploring topics you don't know about
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  Academic research with sources
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Pricing Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">DeepSeek Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free tier with generous limits</li>
                <li>• API: ~$0.14/million input tokens</li>
                <li>• ~$0.28/million output tokens</li>
                <li>• Free local deployment option</li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-blue-500 mr-2" />
                <h3 className="text-xl font-semibold">Perplexity Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free tier with daily limits</li>
                <li>• Pro: $20/month</li>
                <li>• Pro includes unlimited searches</li>
                <li>• Access to multiple AI models</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related Comparisons */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">More AI Comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/deepseek-vs-chatgpt" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs ChatGPT</h3>
              <p className="text-muted-foreground">Compare with OpenAI's flagship model.</p>
            </Link>
            <Link to="/deepseek-vs-claude" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Claude</h3>
              <p className="text-muted-foreground">Compare with Anthropic's Claude.</p>
            </Link>
            <Link to="/deepseek-vs-gemini" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Gemini</h3>
              <p className="text-muted-foreground">Compare with Google's Gemini.</p>
            </Link>
            <Link to="/deepseek-vs-stepfun" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Stepfun</h3>
              <p className="text-muted-foreground">Compare with Stepfun's Step-3.5 Flash multimodal lab.</p>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-primary/5 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Try DeepSeek Today</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Experience DeepSeek's powerful reasoning capabilities. Free to use via Chrome extension or API.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg"
            >
              <Chrome className="mr-2 h-5 w-5" aria-hidden="true" />
              Add to Chrome - It's Free
            </a>
            <Link
              to="/deepseek-r1"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all"
            >
              Learn About DeepSeek R1
            </Link>
          </div>
        </section>
        {/* Explore further - tracked CTAs */}
        <section className="mt-12 mb-12 p-6 rounded-xl bg-muted/40 border text-center">
          <h3 className="text-lg font-semibold mb-2">Want to go deeper?</h3>
          <p className="text-sm text-muted-foreground mb-4">See what DeepSeek actually costs and how to integrate it.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <TrackedLink
              to="/pricing"
              source="deepseek-vs-perplexity"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              See DeepSeek pricing →
            </TrackedLink>
            <TrackedLink
              to="/docs"
              source="deepseek-vs-perplexity"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full border-2 border-primary text-primary bg-background hover:bg-accent transition-colors"
            >
              Read the API docs →
            </TrackedLink>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekVsPerplexity;
